import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  FaDownload,
  FaEllipsisV,
  FaPlus,
  FaShieldAlt,
  FaTrash,
  FaEnvelope,
  FaPen,
  FaChevronDown,
  FaTimes,
  FaArrowRight,
  FaEye,
  FaIdBadge,
  FaUserCog,
  FaUserPlus,
} from "react-icons/fa";
import "../styles/UserManagement.css";
import Pagination from "../components/Pagination";
import { createOrgUser, deleteOrgUser, listOrgUsers, updateOrgUser } from "../api/services/usersService";
import { getUserData } from "../utils/authStorage";
import { useToast } from "../context/ToastContext";

const DEMO_USERS = [
  {
    _id: "demo-1",
    name: "Sarah Jenkins",
    username: "sarah.jenkins@mindscrfat.com",
    email: "sarah.jenkins@mindscrfat.com",
    user_type: "Security Lead",
    roles: ["all"],
    permissions_active: 8,
    permissions_total: 12,
    last_login: "2 hours ago",
    last_ip: "IP: 192.168.1.45",
    session_status: "Active Session",
    last_activity: "2 hours ago",
  },
  {
    _id: "demo-2",
    name: "Marcus Vane",
    username: "marcus.vane@mindscrfat.com",
    email: "marcus.vane@mindscrfat.com",
    user_type: "Data Analyst",
    roles: ["view_reports", "view_merchants"],
    permissions_active: 4,
    permissions_total: 12,
    last_login: "3 days ago",
    last_ip: "Offline",
    session_status: "",
    last_activity: "3 days ago",
  },
  {
    _id: "demo-3",
    name: "Elena Rossi",
    username: "elena.rossi@mindscrfat.com",
    email: "elena.rossi@mindscrfat.com",
    user_type: "Compliance",
    roles: ["audit_logs", "view_reports"],
    permissions_active: 11,
    permissions_total: 12,
    last_login: "Just now",
    last_ip: "",
    session_status: "Active Session",
    last_activity: "Just now",
  },
  {
    _id: "demo-4",
    name: "David Chen",
    username: "david.chen@mindscrfat.com",
    email: "david.chen@mindscrfat.com",
    user_type: "Junior Admin",
    roles: ["all"],
    permissions_active: 3,
    permissions_total: 12,
    last_login: "15 mins ago",
    last_ip: "IP: 10.0.0.12",
    session_status: "",
    last_activity: "15 mins ago",
  },
];

const SORT_OPTIONS = [
  { label: "Last Activity", value: "last_activity" },
  { label: "Name", value: "name" },
];

const DEPARTMENT_OPTIONS = [
  { label: "Engineering", value: "Engineering" },
  { label: "Finance", value: "Finance" },
  { label: "Operations", value: "Operations" },
];

const ROLE_OPTIONS = [
  { label: "Super Admin", value: "super_admin", icon: FaIdBadge },
  { label: "Moderator", value: "moderator", icon: FaUserCog },
  { label: "Auditor", value: "auditor", icon: FaEye },
];

const PERMISSION_SECTIONS = [
  {
    title: "Entity Management",
    accentClass: "um-drawer__groupTitle",
    items: [
      { key: "createOrganization", label: "Create Organization" },
      { key: "editOrganization", label: "Edit Organization" },
      { key: "blockOrganization", label: "Block Organization" },
    ],
  },
  {
    title: "Financial Reports",
    accentClass: "um-drawer__groupTitle",
    items: [
      { key: "downloadReports", label: "Download Reports" },
      { key: "viewAnalytics", label: "View Analytics" },
    ],
  },
  {
    title: "System Administration",
    accentClass: "um-drawer__groupTitle",
    items: [
      { key: "manageUsers", label: "Manage Users" },
    ],
  },
];

const DEFAULT_PERMISSIONS = {
  createOrganization: true,
  editOrganization: true,
  blockOrganization: false,
  downloadReports: true,
  viewAnalytics: true,
  manageUsers: false,
};

const ADD_USER_INITIAL_VALUES = {
  name: "",
  username: "",
  department: "Engineering",
  roles: ["super_admin"],
  permissions: DEFAULT_PERMISSIONS,
};

const inferDialogRole = (user) => {
  const firstKnownRole = Array.isArray(user?.roles)
    ? user.roles.find((role) => ROLE_OPTIONS.some((option) => option.value === role))
    : null;

  if (firstKnownRole) return firstKnownRole;

  const type = String(user?.user_type || "").toLowerCase();
  if (type.includes("audit") || type.includes("compliance")) return "auditor";
  if (type.includes("admin")) return "super_admin";
  return "moderator";
};

const countActivePermissions = (permissions) => Object.values(permissions || {}).filter(Boolean).length;

const addUserValidationSchema = Yup.object().shape({
  name: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Only letters and spaces allowed")
    .required("Full name is required"),
  username: Yup.string()
    .email("Enter a valid email address")
    .required("Enterprise email is required"),
  department: Yup.string().required("Department is required"),
  roles: Yup.array()
    .of(Yup.string().oneOf(ROLE_OPTIONS.map((role) => role.value), "Invalid role"))
    .min(1, "Select an initial role")
    .required("Initial role is required"),
});

const PAGE_SIZE = 4;

const roleTag = (value = "") => {
  const raw = String(value).toLowerCase();
  if (raw.includes("admin")) return "Admin";
  if (raw.includes("support")) return "Support";
  if (raw.includes("analyst") || raw.includes("manager")) return "Manager";
  return "Manager";
};

const statusTag = (user) => {
  const raw = String(user?.session_status || user?.last_ip || "").toLowerCase();
  if (raw.includes("active")) return "Active";
  if (raw.includes("offline") || raw.includes("revoked")) return "Inactive";
  return "Pending";
};

const UsersList = ({ isSidebarOpen }) => {
  const [loading, setLoading] = useState(true);
  const [users, setAllUsers] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("last_activity");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerUser, setDrawerUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogMode, setAddDialogMode] = useState("create");
  const [addDialogUser, setAddDialogUser] = useState(null);
  const [addDialogStep, setAddDialogStep] = useState(1);
  const [departmentMenuOpen, setDepartmentMenuOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const { showSuccess, showError } = useToast();
  const sortMenuRef = React.useRef(null);
  const departmentMenuRef = React.useRef(null);

  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const orgId = user?.profile?.orgId;
  const userType = String(user?.user_type || user?.type || "").toLowerCase();
  const isOrgAdmin = userType === "org" || userType === "org-admin" || userType === "organizationadmin";

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!orgId) {
          setAllUsers(DEMO_USERS);
          return;
        }
        const res = await listOrgUsers(orgId);
        if (Array.isArray(res?.data) && res.data.length) {
          setAllUsers(res.data);
        } else {
          setAllUsers(DEMO_USERS);
        }
      } catch (err) {
        console.error("Error fetching users:", err);
        setAllUsers(DEMO_USERS);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId]);

  useEffect(() => {
    setPage(0);
  }, [activeFilter, sortBy, users.length]);

  useEffect(() => {
    const handleDocMouseDown = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
        setSortMenuOpen(false);
      }
      if (departmentMenuRef.current && !departmentMenuRef.current.contains(e.target)) {
        setDepartmentMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocMouseDown);
    return () => document.removeEventListener("mousedown", handleDocMouseDown);
  }, []);

  useEffect(() => {
    if (!drawerOpen && !addDialogOpen && !deleteTarget) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        setAddDialogOpen(false);
        setDeleteTarget(null);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen, addDialogOpen, deleteTarget]);

  const openDrawer = (u) => {
    setDrawerUser(u);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const openDeleteDialog = (userRecord) => {
    setDrawerOpen(false);
    setDeleteTarget(userRecord);
  };

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
  };

  const openAddDialog = () => {
    setDrawerOpen(false);
    setAddDialogMode("create");
    setAddDialogUser(null);
    setAddDialogStep(1);
    setAddDialogOpen(true);
  };

  const openEditDialog = (userRecord) => {
    setDrawerOpen(false);
    setAddDialogMode("edit");
    setAddDialogUser(userRecord);
    setAddDialogStep(1);
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setAddDialogMode("create");
    setAddDialogUser(null);
    setAddDialogStep(1);
    setDepartmentMenuOpen(false);
  };

  const refreshUsers = async () => {
    const res = await listOrgUsers(orgId);
    if (res?.data) setAllUsers(res.data);
  };

  const handleSaveUser = async (values, { resetForm }) => {
    setCreateLoading(true);

    try {
      const roleLabel = ROLE_OPTIONS.find((role) => role.value === values.roles[0])?.label || "Super Admin";
      const activePermissionCount = countActivePermissions(values.permissions);

      if (addDialogMode === "edit" && addDialogUser) {
        const updatedPayload = {
          name: values.name.trim(),
          department: values.department,
          roles: values.roles,
          permissions: values.permissions,
        };

        if (!orgId) {
          setAllUsers((prev) => prev.map((item) => (
            item._id === addDialogUser._id
              ? {
                  ...item,
                  name: values.name.trim(),
                  department: values.department,
                  roles: values.roles,
                  permissions: values.permissions,
                  permissions_active: activePermissionCount,
                  permissions_total: Object.keys(DEFAULT_PERMISSIONS).length,
                  user_type: roleLabel,
                }
              : item
          )));
          showSuccess("Updated", "User updated successfully");
          resetForm();
          closeAddDialog();
          return;
        }

        const updateRes = await updateOrgUser(addDialogUser._id, updatedPayload);
        if (updateRes?.isSuccess || updateRes?.data || updateRes?.message) {
          await refreshUsers();
          showSuccess("Updated", updateRes?.message || "User updated successfully");
          resetForm();
          closeAddDialog();
        } else {
          showError("Error", updateRes?.message || "Failed to update user", 3000);
        }
        return;
      }

      const payload = {
        username: values.username.trim(),
        name: values.name.trim(),
        orgID: orgId,
        department: values.department,
        roles: values.roles,
        permissions: values.permissions,
      };

      if (!orgId) {
        const demoUser = {
          _id: `demo-${Date.now()}`,
          name: values.name.trim(),
          username: values.username.trim(),
          email: values.username.trim(),
          user_type: roleLabel,
          roles: values.roles,
          permissions: values.permissions,
          permissions_active: activePermissionCount,
          permissions_total: Object.keys(DEFAULT_PERMISSIONS).length,
          last_login: "Just now",
          last_ip: "",
          session_status: "Active Session",
          last_activity: "Just now",
        };

        setAllUsers((prev) => [demoUser, ...prev]);
        showSuccess("Created", "User created successfully");
        resetForm();
        closeAddDialog();
        return;
      }

      const res = await createOrgUser(payload);
      if (res?.isSuccess || res?.data || res?.message) {
        await refreshUsers();
        showSuccess("Created", res?.message || "User created successfully");
        resetForm();
        closeAddDialog();
      } else {
        showError("Error", res?.message || "Failed to create user", 3000);
      }
    } catch (err) {
      console.error("Error creating user:", err);
      showError("Error", "Something went wrong while creating the user", 3000);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const res = await deleteOrgUser(userId);
      if (res?.isSuccess) {
        showSuccess("Deleted", "User deleted successfully");
        refreshUsers();
      } else {
        showError("Error", res?.message || "Failed to delete user", 3000);
      }
    } catch {
      showError("Error", "Something went wrong", 3000);
    }
  };

  const userCellTemplate = (rowData) => {
    const name = rowData?.name || rowData?.username || "—";
    const email = rowData?.email || rowData?.username || "";
    return (
      <div className="um-usercell">
        <div className="um-avatar">
          {String(name).slice(0, 1).toUpperCase()}
          <span className="um-online" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="um-name">{name}</div>
          <div className="um-email">{email}</div>
        </div>
      </div>
    );
  };

  const roleBadgeTemplate = (rowData) => {
    if (!isOrgAdmin) {
      const type = String(rowData?.user_type || "").toLowerCase();
      const label = rowData?.user_type || "—";
      const cls =
        type.includes("security")
          ? "um-badge um-badge--cyan"
          : type.includes("compliance")
          ? "um-badge um-badge--amber"
          : "um-badge";
      return <span className={cls}>{label}</span>;
    }

    const label = roleTag(rowData?.user_type || "");
    const cls =
      label === "Admin"
        ? "um-badge um-badge--admin"
        : label === "Support"
        ? "um-badge um-badge--support"
        : "um-badge um-badge--manager";
    return <span className={cls}>{label}</span>;
  };

  const permissionsTemplate = (rowData) => {
    if (!isOrgAdmin) {
      const active = Number(rowData?.permissions_active ?? rowData?.active_permissions ?? 0);
      const total = Number(rowData?.permissions_total ?? 12);
      const pct = total ? Math.min(100, Math.max(0, (active / total) * 100)) : 0;

      return (
        <div className="um-perms">
          <div className="um-perms__meta">
            <span className="um-perms__count">{active}/{total} active</span>
          </div>
          <div className="um-perms__bar">
            <div className="um-perms__fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      );
    }

    const items = Array.isArray(rowData?.roles) && rowData.roles.length
      ? rowData.roles.slice(0, 3)
      : ["merchant", "user", "audit"];

    const labels = items.map((item) =>
      String(item)
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (ch) => ch.toUpperCase())
    );

    return (
      <div className="um-permchips">
        {labels.map((label) => (
          <span key={label} className="um-permchip">{label}</span>
        ))}
      </div>
    );
  };

  const statusTemplate = (rowData) => {
    if (!isOrgAdmin) {
      const t = rowData?.last_login || rowData?.last_activity || "—";
      const sub = rowData?.last_ip || "";
      const status = rowData?.session_status || "";
      return (
        <div className="um-lastlogin">
          <div className="um-lastlogin__top">{t}</div>
          {sub ? <div className="um-lastlogin__sub">{sub}</div> : null}
          {status ? <div className="um-lastlogin__status">{status}</div> : null}
        </div>
      );
    }

    const label = statusTag(rowData);
    const cls =
      label === "Active"
        ? "um-status um-status--active"
        : label === "Pending"
        ? "um-status um-status--pending"
        : "um-status um-status--inactive";

    return (
      <span className={cls}>{label}</span>
    );
  };

  /* ── Action buttons ── */
  const actionBodyTemplate = (rowData) => {
    if (String(rowData.user_type || "").toLowerCase() === "admin") return null;

    if (!isOrgAdmin) {
      return (
        <div className="um-actions">
          <button
            className="um-iconbtn"
            title="Notify"
            type="button"
            onClick={() => openDrawer(rowData)}
          >
            <FaEnvelope />
          </button>
          <button
            className="um-iconbtn"
            title="Edit"
            type="button"
            onClick={() => openEditDialog(rowData)}
          >
            <FaPen />
          </button>
          <button
            className="um-iconbtn"
            title="Delete User"
            type="button"
            onClick={() => openDeleteDialog(rowData)}
          >
            <FaTrash />
          </button>
        </div>
      );
    }

    return (
      <div className="um-actions">
        <button
          className="um-iconbtn"
          title="Open user controls"
          type="button"
          onClick={() => openDrawer(rowData)}
        >
          <FaEllipsisV />
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p className="msd-muted">Loading users…</p>
      </div>
    );
  }

  const filteredUsers = users.filter((u) => {
    if (activeFilter === "all") return true;
    const type = String(u?.user_type || "").toLowerCase();
    if (activeFilter === "security") return type.includes("security");
    if (activeFilter === "analyst") return type.includes("analyst");
    if (activeFilter === "compliance") return type.includes("compliance");
    return true;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!isOrgAdmin && sortBy === "name") {
      const an = String(a?.name || a?.username || "").toLowerCase();
      const bn = String(b?.name || b?.username || "").toLowerCase();
      return an.localeCompare(bn);
    }
    const al = String(a?.last_activity || "").toLowerCase();
    const bl = String(b?.last_activity || "").toLowerCase();
    return bl.localeCompare(al);
  });

  const total = sortedUsers.length;
  const pageUsers = sortedUsers.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const sortLabel = SORT_OPTIONS.find((o) => o.value === sortBy)?.label || "Last Activity";

  const adminsCount = sortedUsers.filter((u) => roleTag(u?.user_type) === "Admin").length;
  const managersCount = sortedUsers.filter((u) => roleTag(u?.user_type) === "Manager").length;
  const supportCount = sortedUsers.filter((u) => roleTag(u?.user_type) === "Support").length;
  const inactiveCount = sortedUsers.filter((u) => statusTag(u) === "Inactive").length;

  const securityPercent = total ? Math.round(((total - inactiveCount) / total) * 100) : 92;

  const auditTimeline = [
    {
      when: "Today, 10:42 AM",
      text: `Invite sent to ${sortedUsers[0]?.name || "new user"}`,
      action: "ACTION: USER_INVITE",
      tone: "blue",
    },
    {
      when: "Yesterday, 4:15 PM",
      text: `Role updated for ${sortedUsers[1]?.name || "Marcus Smith"}`,
      action: "ACTION: ROLE_CHANGE",
      tone: "teal",
    },
    {
      when: "Oct 24, 09:12 AM",
      text: `Account deactivated for ${sortedUsers[2]?.name || "John Wick"}`,
      action: "ACTION: USER_DEACTIVATE",
      tone: "rose",
    },
    {
      when: "Oct 23, 11:30 PM",
      text: "Password reset completed",
      action: "ACTION: SECURITY_AUTH",
      tone: "violet",
    },
  ];

  const drawerName = drawerUser?.name || drawerUser?.username || "—";
  const drawerEmail = drawerUser?.email || drawerUser?.username || "";
  const drawerRole = drawerUser?.user_type || "";
  const dialogInitialValues = addDialogMode === "edit" && addDialogUser
    ? {
        name: addDialogUser?.name || "",
        username: addDialogUser?.email || addDialogUser?.username || "",
        department: addDialogUser?.department || "Engineering",
        roles: [inferDialogRole(addDialogUser)],
        permissions: {
          ...DEFAULT_PERMISSIONS,
          ...(addDialogUser?.permissions || {}),
        },
      }
    : ADD_USER_INITIAL_VALUES;

  return (
    <div className={`msd-container ${isSidebarOpen ? "shifted" : ""} um-page${isOrgAdmin ? " um-v2" : ""}`}>
      {isOrgAdmin ? (
        <>
          <div className="um-v2__topbar">
            <div className="um-v2__crumbs">Organization &nbsp;›&nbsp; User Management</div>
            <div className="um-v2__actions">
              <button type="button" className="um-v2__btn um-v2__btn--ghost">
                <FaDownload /> Export
              </button>
              <button className="um-v2__btn um-v2__btn--primary" type="button" onClick={openAddDialog}>
                <FaUserPlus /> Invite User
              </button>
            </div>
          </div>

          <div className="um-header">
            <div>
              <h2 className="um-title">User Management</h2>
              <div className="um-subtitle">
                Control access and permission levels across your organization.
              </div>
            </div>
          </div>

          <div className="um-v2__stats">
            <article className="um-v2__stat um-v2__stat--admins">
              <span>Admins</span>
              <strong>{adminsCount}</strong>
              <small>Full system access</small>
            </article>
            <article className="um-v2__stat um-v2__stat--managers">
              <span>Managers</span>
              <strong>{managersCount}</strong>
              <small>Team & resource management</small>
            </article>
            <article className="um-v2__stat um-v2__stat--support">
              <span>Support</span>
              <strong>{supportCount}</strong>
              <small>Ticket & client resolution</small>
            </article>
            <article className="um-v2__stat um-v2__stat--inactive">
              <span>Inactive</span>
              <strong>{inactiveCount}</strong>
              <small>Access revoked/pending</small>
            </article>
          </div>

          <div className="um-v2__grid">
            <div className={drawerOpen ? "um-tablewrap um-tablewrap--draweropen" : "um-tablewrap"}>
              <DataTable
                value={pageUsers}
                dataKey="_id"
                className="um-datatable"
                emptyMessage="No users found."
                rowHover
              >
                <Column header="User" body={userCellTemplate} style={{ width: "34%" }} />
                <Column header="Role" body={roleBadgeTemplate} style={{ width: "16%" }} sortable sortField="user_type" />
                <Column header="Permissions" body={permissionsTemplate} style={{ width: "25%" }} />
                <Column header="Status" body={statusTemplate} style={{ width: "15%" }} />
                <Column header="Actions" body={actionBodyTemplate} style={{ width: "10%" }} />
              </DataTable>

              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={total}
                onPageChange={setPage}
                itemLabel="users"
              />
            </div>

            <aside className="um-v2__aside">
              <article className="um-v2__panel">
                <div className="um-v2__panelhead">
                  <h3>Audit Timeline</h3>
                  <button type="button">View all</button>
                </div>
                <div className="um-v2__timeline">
                  {auditTimeline.map((item) => (
                    <div key={`${item.when}-${item.action}`} className="um-v2__timelineitem">
                      <span className={`um-v2__dot um-v2__dot--${item.tone}`} />
                      <div>
                        <small>{item.when}</small>
                        <p>{item.text}</p>
                        <em>{item.action}</em>
                      </div>
                    </div>
                  ))}
                </div>
              </article>

              <article className="um-v2__panel um-v2__panel--security">
                <h3><FaShieldAlt /> Security Health</h3>
                <div className="um-v2__securitylabel">
                  <span>2FA Adoption</span>
                  <strong>{securityPercent}%</strong>
                </div>
                <div className="um-v2__securitybar">
                  <span style={{ width: `${securityPercent}%` }} />
                </div>
                <p>{Math.max(0, total - (total - inactiveCount))} users still require 2FA setup. Reminders sent automatically.</p>
              </article>
            </aside>
          </div>
        </>
      ) : (
        <>
          <div className="um-header">
            <div>
              <h2 className="um-title">User Management</h2>
              <div className="um-subtitle">
                Manage access protocols and security credentials for {total} active administrators.
              </div>
            </div>
            <button className="um-addbtn" type="button" onClick={openAddDialog}>
              <FaPlus />
              Add User
            </button>
          </div>

          <div className="um-filtersbar">
            <div className="um-filtersleft">
              <div className="um-filterslabel">FILTERS:</div>
              <div className="um-pillrow">
                <button className={activeFilter === "all" ? "um-pill active" : "um-pill"} type="button" onClick={() => setActiveFilter("all")}>All Roles</button>
                <button className={activeFilter === "security" ? "um-pill active" : "um-pill"} type="button" onClick={() => setActiveFilter("security")}>Security Leads</button>
                <button className={activeFilter === "analyst" ? "um-pill active" : "um-pill"} type="button" onClick={() => setActiveFilter("analyst")}>Data Analysts</button>
                <button className={activeFilter === "compliance" ? "um-pill active" : "um-pill"} type="button" onClick={() => setActiveFilter("compliance")}>Compliance</button>
              </div>
            </div>

            <div className="um-filtersright">
              <div className="um-sortwrap" ref={sortMenuRef}>
                <button
                  type="button"
                  className={sortMenuOpen ? "um-sortpill open" : "um-sortpill"}
                  onClick={() => setSortMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={sortMenuOpen}
                >
                  <span className="um-sortpill__label">SORT BY:</span>
                  <span className="um-sortpill__value">{sortLabel}</span>
                  <FaChevronDown className={sortMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
                </button>

                {sortMenuOpen ? (
                  <div className="um-sortmenu" role="menu">
                    {SORT_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        role="menuitem"
                        className={o.value === sortBy ? "um-sortmenu__item active" : "um-sortmenu__item"}
                        onClick={() => {
                          setSortBy(o.value);
                          setSortMenuOpen(false);
                        }}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className={drawerOpen ? "um-tablewrap um-tablewrap--draweropen" : "um-tablewrap"}>
            <DataTable
              value={pageUsers}
              dataKey="_id"
              className="um-datatable"
              emptyMessage="No users found."
              rowHover
            >
              <Column header="Administrator" body={userCellTemplate} style={{ width: "32%" }} />
              <Column header="Role" body={roleBadgeTemplate} style={{ width: "14%" }} sortable sortField="user_type" />
              <Column header="Permissions" body={permissionsTemplate} style={{ width: "22%" }} />
              <Column header="Last Login" body={statusTemplate} style={{ width: "20%" }} />
              <Column header="Actions" body={actionBodyTemplate} style={{ width: "12%" }} />
            </DataTable>

            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              itemLabel="administrators"
            />
          </div>
        </>
      )}

      {drawerOpen ? (
        <button
          type="button"
          className="um-drawer-scrim"
          aria-label="Close drawer"
          onClick={closeDrawer}
        />
      ) : null}

      <aside className={drawerOpen ? "um-drawer open" : "um-drawer"} aria-hidden={!drawerOpen}>
          <div className="um-drawer__header">
            <div className="um-drawer__kicker">Permissions &amp; Role Assignment</div>
            <button type="button" className="um-drawer__close" aria-label="Close" onClick={closeDrawer}>
              <FaTimes />
            </button>
          </div>

          <div className="um-drawer__user">
            <div className="um-drawer__avatar">
              {String(drawerName).slice(0, 1).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="um-drawer__name">{drawerName}</div>
              <div className="um-drawer__meta">
                {drawerRole ? <span className="um-drawer__role">{drawerRole}</span> : null}
                {drawerEmail ? <span className="um-drawer__email">{drawerEmail}</span> : null}
              </div>
            </div>
          </div>

          <div className="um-drawer__body">
            <div className="um-drawer__group">
              <div className="um-drawer__groupTitle">Entity Management</div>
              <label className="um-toggle">
                <span className="um-toggle__text">Create Organization</span>
                <input className="um-toggle__input" type="checkbox" defaultChecked />
                <span className="um-switch" />
              </label>
              <label className="um-toggle">
                <span className="um-toggle__text">Edit Organization</span>
                <input className="um-toggle__input" type="checkbox" defaultChecked />
                <span className="um-switch" />
              </label>
              <label className="um-toggle">
                <span className="um-toggle__text">Block Organization</span>
                <input className="um-toggle__input" type="checkbox" />
                <span className="um-switch" />
              </label>
            </div>

            <div className="um-drawer__group">
              <div className="um-drawer__groupTitle">Financial Reports</div>
              <label className="um-toggle">
                <span className="um-toggle__text">Download Reports</span>
                <input className="um-toggle__input" type="checkbox" defaultChecked />
                <span className="um-switch" />
              </label>
              <label className="um-toggle">
                <span className="um-toggle__text">View Analytics</span>
                <input className="um-toggle__input" type="checkbox" defaultChecked />
                <span className="um-switch" />
              </label>
            </div>

            <div className="um-drawer__group">
              <div className="um-drawer__groupTitle">System Administration</div>
              <label className="um-toggle">
                <span className="um-toggle__text">Manage Users</span>
                <input className="um-toggle__input" type="checkbox" />
                <span className="um-switch" />
              </label>
            </div>
          </div>

          <div className="um-drawer__footer">
            <button
              type="button"
              className="um-drawer__btn um-drawer__btn--ghost"
              onClick={() => drawerUser && openEditDialog(drawerUser)}
            >
              Edit User
            </button>
            <button
              type="button"
              className="um-drawer__btn um-drawer__btn--ghost"
              onClick={() => drawerUser && openDeleteDialog(drawerUser)}
            >
              Remove
            </button>
            <button type="button" className="um-drawer__btn um-drawer__btn--ghost" onClick={closeDrawer}>Cancel</button>
            <button type="button" className="um-drawer__btn um-drawer__btn--primary" onClick={closeDrawer}>Save Changes</button>
          </div>
      </aside>

      {addDialogOpen ? (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Add User">
          <button type="button" className="um-modaloverlay__scrim" aria-label="Close add user dialog" onClick={closeAddDialog} />
          <Formik
            initialValues={dialogInitialValues}
            validationSchema={addUserValidationSchema}
            onSubmit={handleSaveUser}
            enableReinitialize
          >
            {({ values, errors, touched, setFieldValue, validateForm, setTouched }) => (
              <Form className="um-adddialog-shell um-adddialog-shell--overlay">
                <div className="um-adddialog">
                  <div className="um-adddialog__header">
                    <div>
                      <h2 className="um-adddialog__title">{addDialogMode === "edit" ? "Edit User" : "Add User"}</h2>
                      <div className="um-adddialog__subtitle">{addDialogMode === "edit" ? "Update administrative access for this user." : "Grant administrative access to your organization."}</div>
                    </div>
                    <button type="button" className="um-adddialog__close" onClick={closeAddDialog}>
                      <FaTimes />
                    </button>
                  </div>

                  <div className="um-adddialog__steps">
                    <button
                      type="button"
                      className={addDialogStep === 1 ? "um-adddialog__step is-active" : "um-adddialog__step"}
                      onClick={() => setAddDialogStep(1)}
                    >
                      <span className="um-adddialog__stepIndex">1</span>
                      <span>Basic Info</span>
                    </button>
                    <button
                      type="button"
                      className={addDialogStep === 2 ? "um-adddialog__step is-active" : "um-adddialog__step"}
                      onClick={() => setAddDialogStep(2)}
                    >
                      <span className="um-adddialog__stepIndex">2</span>
                      <span>Permission Assignment</span>
                    </button>
                  </div>

                  <div className="um-adddialog__body">
                    {addDialogStep === 1 ? (
                      <>
                        <div className="um-adddialog__grid">
                          <div className="um-adddialog__field">
                            <label className="um-adddialog__label" htmlFor="add-user-name">Full Name</label>
                            <Field id="add-user-name" name="name" type="text" className="um-adddialog__input" placeholder="e.g. Alexander Vance" />
                            {touched.name && errors.name ? <div className="error-text">{errors.name}</div> : <div className="error-text" />}
                          </div>

                          <div className="um-adddialog__field">
                            <label className="um-adddialog__label" htmlFor="add-user-department">Department</label>
                            <div className="um-formselectwrap" ref={departmentMenuRef}>
                              <button
                                type="button"
                                id="add-user-department"
                                className={departmentMenuOpen ? "um-sortpill um-formselect open" : "um-sortpill um-formselect"}
                                onClick={() => setDepartmentMenuOpen((open) => !open)}
                                aria-haspopup="menu"
                                aria-expanded={departmentMenuOpen}
                              >
                                <span className="um-formselect__value">{values.department}</span>
                                <FaChevronDown className={departmentMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
                              </button>

                              {departmentMenuOpen ? (
                                <div className="um-sortmenu um-formselectmenu" role="menu">
                                  {DEPARTMENT_OPTIONS.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      role="menuitem"
                                      className={option.value === values.department ? "um-sortmenu__item active" : "um-sortmenu__item"}
                                      onClick={() => {
                                        setFieldValue("department", option.value);
                                        setDepartmentMenuOpen(false);
                                      }}
                                    >
                                      {option.label}
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                            {touched.department && errors.department ? <div className="error-text">{errors.department}</div> : <div className="error-text" />}
                          </div>
                        </div>

                        <div className="um-adddialog__field um-adddialog__field--full">
                          <label className="um-adddialog__label" htmlFor="add-user-email">Enterprise Email</label>
                          <Field id="add-user-email" name="username" type="email" className="um-adddialog__input" placeholder="vance@omnigate.enterprise" disabled={addDialogMode === "edit"} />
                          {touched.username && errors.username ? <div className="error-text">{errors.username}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field um-adddialog__field--full">
                          <label className="um-adddialog__label">Initial Role</label>
                          <div className="um-rolecards">
                            {ROLE_OPTIONS.map((role) => {
                              const Icon = role.icon;
                              const active = values.roles.includes(role.value);

                              return (
                                <button
                                  key={role.value}
                                  type="button"
                                  className={active ? "um-rolecard active" : "um-rolecard"}
                                  onClick={() => setFieldValue("roles", [role.value])}
                                >
                                  <span className="um-rolecard__icon"><Icon /></span>
                                  <span className="um-rolecard__label">{role.label}</span>
                                </button>
                              );
                            })}
                          </div>
                          {touched.roles && errors.roles ? <div className="error-text">{errors.roles}</div> : <div className="error-text" />}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="um-adddialog__permintro">
                          <div className="um-adddialog__permtitle">Permission Assignment</div>
                          <div className="um-adddialog__permsubtitle">Configure the same permissions available in the side drawer for this administrator.</div>
                        </div>

                        <div className="um-adddialog__permissions">
                          <div className="um-drawer__body um-drawer__body--modal">
                            {PERMISSION_SECTIONS.map((section) => (
                              <div key={section.title} className="um-drawer__group">
                                <div className={section.accentClass}>{section.title}</div>
                                {section.items.map((item) => (
                                  <label key={item.key} className="um-toggle">
                                    <span className="um-toggle__text">{item.label}</span>
                                    <input
                                      className="um-toggle__input"
                                      type="checkbox"
                                      checked={Boolean(values.permissions?.[item.key])}
                                      onChange={(event) => {
                                        setFieldValue(`permissions.${item.key}`, event.target.checked);
                                      }}
                                    />
                                    <span className="um-switch" />
                                  </label>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="um-adddialog__footer">
                    <button type="button" className="um-adddialog__cancel" onClick={closeAddDialog}>Cancel</button>
                    {addDialogStep === 1 ? (
                      <button
                        type="button"
                        className="um-adddialog__submit"
                        onClick={async () => {
                          setTouched({ name: true, username: true, department: true, roles: true }, true);
                          const nextErrors = await validateForm();
                          if (nextErrors.name || nextErrors.username || nextErrors.department || nextErrors.roles) return;
                          setTouched({}, false);
                          setAddDialogStep(2);
                        }}
                      >
                        <span>Next</span>
                        <FaArrowRight />
                      </button>
                    ) : (
                      <div className="um-adddialog__actions">
                        <button type="button" className="um-adddialog__back" onClick={() => setAddDialogStep(1)}>Back</button>
                        <button type="submit" className="um-adddialog__submit" disabled={createLoading}>
                          <span>{createLoading ? "Saving..." : addDialogMode === "edit" ? "Save Changes" : "Create User"}</span>
                          <FaArrowRight />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Delete User">
          <button type="button" className="um-modaloverlay__scrim" aria-label="Close delete confirmation" onClick={closeDeleteDialog} />
          <div className="um-confirmdialog">
            <div className="um-confirmdialog__header">
              <h3 className="um-confirmdialog__title">Delete User</h3>
              <button type="button" className="um-adddialog__close" onClick={closeDeleteDialog}>
                <FaTimes />
              </button>
            </div>
            <div className="um-confirmdialog__body">
              <p className="um-confirmdialog__text">
                Are you sure you want to delete <strong>{deleteTarget.name || deleteTarget.username}</strong>?
              </p>
              <p className="um-confirmdialog__subtext">
                This action will remove the user from the administrator list.
              </p>
            </div>
            <div className="um-confirmdialog__footer">
              <button type="button" className="um-adddialog__back" onClick={closeDeleteDialog}>Cancel</button>
              <button
                type="button"
                className="um-confirmdialog__delete"
                onClick={() => {
                  handleDeleteUser(deleteTarget._id);
                  closeDeleteDialog();
                }}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UsersList;
